import React, { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "./components/ui/card";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Label } from "./components/ui/label";
import { Alert } from "./components/ui/alert";
import { LogIn, ScanFace } from "lucide-react";
import { toast } from "sonner";
import { 
  getSavedCredentials, 
  performBiometricAuth 
} from './lib/secureAuth';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LOGO_URL = process.env.REACT_APP_LOGO_URL || "https://customer-assets.emergentagent.com/job_workbill-system/artifacts/h58fgzmy_PM%20Logo.jpg";

// Debug environment
console.log('🔍 Login.js Environment:', {
  REACT_APP_BACKEND_URL: process.env.REACT_APP_BACKEND_URL,
  API: `${BACKEND_URL}/api`
});

// Marca (por sessão do app / contexto JS) se o Face ID automático já foi tentado.
// Cold start (app aberto do zero ou reload) => auto. Voltar ao login com app aberto => só botão.
let biometricAutoAttempted = false;

function Login({ onLogin, language, changeLanguage, t }) {
  
  const [credentials, setCredentials] = useState({
    username: "",
    password: ""
  });
  const [loading, setLoading] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [error, setError] = useState("");
  const [showResetModal, setShowResetModal] = useState(false);
  const [resetUsername, setResetUsername] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  
  // Biometric detection states (FASE 1 - apenas detectar)
  const [biometricAvailable, setBiometricAvailable] = useState(false);
  const [biometricType, setBiometricType] = useState(null);
  const [hasSavedCreds, setHasSavedCreds] = useState(false);
  const [showBiometricButton, setShowBiometricButton] = useState(false);

  // Tentar login com Face ID (auto no cold start, ou manual pelo botão)
  const attemptBiometricLogin = async (isManual = false) => {
    try {
      console.log(`🚀 Face ID login attempt (manual=${isManual})...`);

      const savedCredentials = await getSavedCredentials();
      if (!savedCredentials) {
        console.log('ℹ️ No saved credentials found');
        return;
      }

      const biometricSuccess = await performBiometricAuth({ username: savedCredentials.username });

      if (biometricSuccess) {
        const response = await tryLogin(savedCredentials.username, savedCredentials.password);
        if (response.status === 200) {
          console.log('🎉 Login com Face ID bem-sucedido!');
          sessionStorage.setItem("access_token", response.data.access_token);
          onLogin(response.data, { isAutoLogin: true });
        } else {
          toast.error('Credenciais salvas inválidas. Faça login novamente.');
          setShowBiometricButton(true);
        }
      } else {
        console.log('❌ Face ID cancelado/falhou - permanece na tela de login');
        // Mostra o botão para o usuário tentar novamente manualmente
        setShowBiometricButton(true);
      }
    } catch (error) {
      console.error('❌ Erro no login por Face ID:', error);
      setShowBiometricButton(true);
    }
  };

  const handleBiometricButton = () => {
    attemptBiometricLogin(true);
  };

  // Detectar biometria na inicialização; auto-login só no cold start
  useEffect(() => {
    (async () => {
      const available = await checkBiometricAvailability();
      let saved = null;
      try { saved = await getSavedCredentials(); } catch (e) { /* noop */ }
      setHasSavedCreds(!!saved);
      if (!available || !saved) return;
      if (!biometricAutoAttempted) {
        // Cold start => dispara Face ID automaticamente
        biometricAutoAttempted = true;
        attemptBiometricLogin(false);
      } else {
        // Voltou ao login com o app já aberto (ex.: logout) => só mostra o botão
        setShowBiometricButton(true);
      }
    })();
  }, []);

  const checkBiometricAvailability = async () => {
    try {
      if (!window.Capacitor) {
        return false;
      }

      const { NativeBiometric } = await import('@capgo/capacitor-native-biometric');
      const result = await NativeBiometric.isAvailable();

      setBiometricAvailable(result.isAvailable);
      if (result.isAvailable) {
        setBiometricType(result.biometryType);
      }
      return result.isAvailable;
    } catch (error) {
      setBiometricAvailable(false);
      return false;
    }
  };

  const normalizeUsername = (u) => (u || '').trim();

  const tryLogin = async (username, password) => {
    const payload = { username, password };
    return axios.post(`${API}/login`, payload);
  };


  const handleLogin = async (e) => {
    e.preventDefault();
    if (!credentials.username || !credentials.password) {
      setError("Please enter both username and password");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const u = normalizeUsername(credentials.username);
      
      // Direct login attempt without alternative tries
      console.log(`🔍 Tentando login: ${u} / [senha oculta]`);
      const response = await axios.post(`${API}/login`, {
        username: u,
        password: credentials.password
      });
      
      const responseData = response.data || {};
      const access_token = responseData.access_token;
      const user_info = responseData.user_info;
      
      if (!access_token || !user_info) {
        throw new Error('Dados de login incompletos');
      }
      
      sessionStorage.setItem('access_token', access_token);
      sessionStorage.setItem('user_info', JSON.stringify(user_info));
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      
      // Garantir que sempre abre na Home e no topo da página
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }, 100);
      
      onLogin(user_info, { username: u, password: credentials.password }); // Pass credentials for secure storage
    } catch (error) {
      console.error("Login error:", error);
      if (error.response?.status === 401) {
        setError("Invalid username or password");
      } else {
        setError("Login failed. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setCredentials(prev => ({
      ...prev,
      [field]: value
    }));
    setError("");
  };

  const handlePasswordReset = async () => {
    if (!resetUsername.trim()) {
      toast.error("Por favor, digite o nome de usuário");
      return;
    }
    
    try {
      setResetLoading(true);
      setError("");
      
      const response = await axios.post(`${API}/request-password-reset`, {
        username: resetUsername.trim(),
        email: "pmhomeremodeling@gmail.com"
      });
      
      toast.success("📧 Link de redefinição enviado para pmhomeremodeling@gmail.com!");
      setShowResetModal(false);
      setResetUsername("");
      console.log("Password reset link sent successfully");
    } catch (error) {
      console.error("Password reset error:", error);
      toast.success("📧 Link de redefinição enviado para pmhomeremodeling@gmail.com!");
      setShowResetModal(false);
      setResetUsername("");
    } finally {
      setResetLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-slate-100 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Language Flags */}
        <div className="flex justify-center gap-3 mb-4">
          <button
            onClick={() => changeLanguage('pt-BR')}
            className={`flex items-center gap-2 px-3 py-2 rounded-lg border-2 transition-all ${
              language === 'pt-BR' 
                ? 'border-green-500 bg-green-50 shadow-md' 
                : 'border-gray-200 bg-white hover:border-green-300'
            }`}
            title="Português (Brasil)"
          >
            <span className="text-lg">🇧🇷</span>
            <span className="text-xs font-medium">Português</span>
          </button>
          <button
            onClick={() => changeLanguage('en-US')}
            className={`flex items-center gap-2 px-3 py-2 rounded-lg border-2 transition-all ${
              language === 'en-US' 
                ? 'border-blue-500 bg-blue-50 shadow-md' 
                : 'border-gray-200 bg-white hover:border-blue-300'
            }`}
            title="English (United States)"
          >
            <span className="text-lg">🇺🇸</span>
            <span className="text-xs font-medium">English</span>
          </button>
        </div>
        
        <Card className="shadow-xl border-0">
          <CardHeader className="text-center pb-2">
            <div className="mx-auto mb-4">
              <img 
                src={LOGO_URL} 
                alt="PM Logo" 
                className="h-20 w-auto mx-auto"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = '/icon-192x192.png';
                }}
              />
            </div>
            <CardTitle className="text-2xl font-bold text-slate-800">
              PM Home Remodeling
            </CardTitle>
            <p className="text-slate-600 text-sm mt-2">
              {language === 'pt-BR' ? 'Portal de Acesso' : 'Access Portal'}
            </p>
          </CardHeader>
          <CardContent className="pt-4">
            {error && (
              <Alert className="mb-4 border-red-200 bg-red-50">
                <div className="text-red-800 text-sm">{error}</div>
              </Alert>
            )}
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <Label htmlFor="username" className="text-slate-700 font-medium">
                  {t('username')}
                </Label>
                <div className="relative mt-1">
                  <Input
                    id="username"
                    type="text"
                    placeholder={language === 'pt-BR' ? 'Digite seu usuário PM (ex: PM1)' : 'Enter your PM username (e.g., PM1)'}
                    value={credentials.username}
                    onChange={(e) => handleInputChange('username', e.target.value)}
                    className="pl-3 h-11"
                    disabled={loading}
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="password" className="text-slate-700 font-medium">
                  {t('password')}
                </Label>
                <div className="relative mt-1">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder={language === 'pt-BR' ? 'Digite sua senha' : 'Enter your password'}
                    value={credentials.password}
                    onChange={(e) => handleInputChange('password', e.target.value)}
                    className="pl-3 pr-10 h-11"
                    disabled={loading}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600"
                    tabIndex={-1}
                  >
                    {showPassword ? (
                      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L12 12m-2.122-2.122L7.757 7.757M12 12l2.122 2.122m-2.122-2.122L7.757 7.757m6.364 6.364L16.243 16.243" />
                      </svg>
                    ) : (
                      <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    )}
                  </button>
                </div>
              </div>
              <Button
                type="submit"
                className="w-full h-11 bg-blue-600 hover:bg-blue-700 text-white font-medium mt-6"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    {language === 'pt-BR' ? 'Entrando...' : 'Signing In...'}
                  </>
                ) : (
                  <>
                    <LogIn className="h-4 w-4 mr-2" />
                    {t('signIn')}
                  </>
                )}
              </Button>
            </form>

            {/* Botão discreto de Face ID (aparece ao voltar ao login com o app aberto) */}
            {biometricAvailable && hasSavedCreds && showBiometricButton && (
              <Button
                type="button"
                variant="outline"
                onClick={handleBiometricButton}
                data-testid="face-id-login-btn"
                className="w-full h-11 mt-3 border-slate-300 text-slate-700 hover:bg-slate-50 font-medium"
              >
                <ScanFace className="h-4 w-4 mr-2" />
                {language === 'pt-BR' ? 'Entrar com Face ID' : 'Sign in with Face ID'}
              </Button>
            )}

            {/* Link Redefinir Senha - Discreto */}
            <div className="mt-4 pt-4 border-t text-center">
              <button
                type="button"
                onClick={() => setShowResetModal(true)}
                className="text-slate-500 hover:text-blue-600 text-sm underline-offset-4 hover:underline transition-colors cursor-pointer bg-transparent border-none"
              >
                {t('forgotPassword')}
              </button>
            </div>

            {/* FASE 1: Indicador de biometria disponível */}
            {/* Biometric message removed */}

          </CardContent>
        </Card>

        {/* Atalhos para os outros portais (apps separados por rota) */}
        <div className="grid grid-cols-2 gap-3 mt-4" data-testid="portal-shortcuts">
          {[
            { id: 'sales', href: '/sales', logo: '/sales-logo.png', name: language === 'pt-BR' ? 'Portal de Vendas' : 'Sales Portal', desc: language === 'pt-BR' ? 'Vendas & medidas' : 'Sales & measuring', ring: 'hover:border-teal-400 hover:shadow-teal-100' },
            { id: 'studio', href: '/studio', logo: '/studio-logo.png', name: 'PM Studio', desc: language === 'pt-BR' ? 'Portal do cliente' : 'Customer portal', ring: 'hover:border-sky-400 hover:shadow-sky-100' },
          ].map((p) => (
            <a key={p.id} href={p.href} data-testid={`portal-link-${p.id}`} className={`group flex items-center gap-3 rounded-2xl border border-slate-200 bg-white/90 backdrop-blur px-3 py-2.5 shadow-sm transition-[border-color,box-shadow,transform] hover:-translate-y-0.5 hover:shadow-lg ${p.ring}`}>
              <img src={p.logo} alt={p.name} className="h-12 w-12 object-contain shrink-0" />
              <div className="min-w-0">
                <p className="text-sm font-semibold text-slate-800 leading-tight">{p.name}</p>
                <p className="text-[11px] text-slate-500 truncate">{p.desc}</p>
                <p className="text-[11px] font-semibold text-slate-700 group-hover:text-slate-900 mt-0.5">{language === 'pt-BR' ? 'Acessar →' : 'Open →'}</p>
              </div>
            </a>
          ))}
        </div>
        {showResetModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold">
                  🔑 {language === 'pt-BR' ? 'Redefinir Senha' : 'Reset Password'}
                </h3>
                <button 
                  onClick={() => setShowResetModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ✕
                </button>
              </div>
              
              <div className="space-y-4">
                <div>
                  <Label htmlFor="resetUsername" className="text-slate-700 font-medium">
                    {t('username')}
                  </Label>
                  <Input
                    id="resetUsername"
                    type="text"
                    placeholder={language === 'pt-BR' ? 'Digite o usuário (ex: ADMIN, PM1...)' : 'Enter username (e.g., ADMIN, PM1...)'}
                    value={resetUsername}
                    onChange={(e) => setResetUsername(e.target.value)}
                    className="mt-1"
                    disabled={resetLoading}
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    {language === 'pt-BR' 
                      ? 'Link será enviado para pmhomeremodeling@gmail.com'
                      : 'Link will be sent to pmhomeremodeling@gmail.com'
                    }
                  </p>
                </div>
                
                <div className="flex gap-3 pt-2">
                  <Button
                    variant="outline"
                    onClick={() => setShowResetModal(false)}
                    disabled={resetLoading}
                    className="flex-1"
                  >
                    {t('cancel')}
                  </Button>
                  <Button
                    onClick={handlePasswordReset}
                    disabled={resetLoading}
                    className="flex-1 bg-blue-600 hover:bg-blue-700"
                  >
                    {resetLoading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        {language === 'pt-BR' ? 'Enviando...' : 'Sending...'}
                      </>
                    ) : (
                      language === 'pt-BR' ? '📧 Enviar Link' : '📧 Send Link'
                    )}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Login;